"use client";
import React, { useEffect, useRef } from "react";
import { defineGptSlotLifecycle, destroyGptSlot, displayGptSlot, loadGpt } from "@/lib/ads/gptRuntime";
import { sizesForViewport } from "@/lib/ads/responsiveMappings";
import { useConsent } from "@/components/privacy/ConsentRuntime";
import { emitBrowserAdDiagnostic } from "@/lib/ads/deliveryDiagnostics";

export default function GamAdRenderer({ decision, onRenderState, onFailure, onRetire, placementIdentifier }) {
  const node = useRef(null);
  const { contract, canRequestAdvertising } = useConsent();
  const callbacks = useRef({ onRenderState, onFailure, onRetire });
  const consentCheck = useRef(canRequestAdvertising);
  consentCheck.current = canRequestAdvertising;
  callbacks.current = { onRenderState, onFailure, onRetire };
  useEffect(() => {
    if (!contract.advertisingAllowed || !decision.liveDeliveryApproved) return;
    let cancelled = false, settled = false, failed = false, listener, requestListener, runtime, observer;
    const report = code => emitBrowserAdDiagnostic(placementIdentifier, code);
    let renderedWidth = 0;
    const elementId = "gam-slot-" + decision.decisionId;
    const permitted = () => !consentCheck.current || consentCheck.current();
    const width = () => Math.min(node.current?.parentElement?.clientWidth || 0, document.documentElement.clientWidth);
    const cleanupSlot = () => {
      if (!runtime) return;
      if (listener) runtime.pubads().removeEventListener("slotRenderEnded", listener);
      if (requestListener) runtime.pubads().removeEventListener("slotRequested", requestListener);
      listener = null;
      requestListener = null;
      destroyGptSlot({ googletag: runtime, elementId });
    };
    const retire = reason => {
      if (cancelled || failed) return;
      failed = true; settled = true; clearTimeout(timer); cleanupSlot();
      report(reason);
      callbacks.current.onRenderState?.(false);
      callbacks.current.onRetire?.(reason);
    };
    const fail = reason => {
      if (cancelled || settled) return;
      failed = true; settled = true; clearTimeout(timer); cleanupSlot();
      report(reason);
      callbacks.current.onFailure?.(reason);
    };
    const timer = setTimeout(() => fail("GAM_TIMEOUT"), 15000);
    const consentChanged = () => { if (!permitted()) retire("CONSENT_WITHDRAWN"); };
    window.addEventListener("surgedome:consent-changed", consentChanged);
    report("GPT_LOADING");
    loadGpt().then(googletag => {
      googletag.cmd.push(() => {
        if (cancelled || settled) return;
        if (!permitted()) { retire("CONSENT_WITHDRAWN"); return; }
        runtime = googletag;
        report("GPT_READY");
        try {
          const available = sizesForViewport(decision.payload.responsiveMapping, width()).filter(size => size[0] <= width());
          if (!available.length) { fail("GAM_SIZE_UNAVAILABLE"); return; }
          googletag.pubads().setPrivacySettings({ nonPersonalizedAds: !contract.personalizedAdvertisingAllowed });
          const slot = defineGptSlotLifecycle({ googletag, elementId, ...decision.payload, sizes: available, responsiveMapping: [] });
          if (!slot) { fail("GAM_SLOT_FAILED"); return; }
          report("GPT_SLOT_DEFINED");
          requestListener = event => { if (!cancelled && !settled && permitted() && event.slot === slot) report("GPT_REQUEST_EVENT"); };
          googletag.pubads().addEventListener("slotRequested", requestListener);
          listener = event => {
            if (cancelled || settled || event.slot !== slot) return;
            if (!permitted()) { retire("CONSENT_WITHDRAWN"); return; }
            if (event.isEmpty) { fail("GAM_EMPTY"); return; }
            if (!Array.isArray(event.size) || !available.some(size => size[0] === event.size[0] && size[1] === event.size[1]) || event.size[0] > width()) {
              fail("GAM_SIZE_MISMATCH"); return;
            }
            renderedWidth = event.size[0];
            report("GAM_RENDERED");
            settled = true; clearTimeout(timer); callbacks.current.onRenderState?.(true);
          };
          googletag.pubads().addEventListener("slotRenderEnded", listener);
          observer = new ResizeObserver(() => {
            if (renderedWidth && renderedWidth > width()) retire("GAM_VIEWPORT_SHRINK");
          });
          if (node.current?.parentElement) observer.observe(node.current.parentElement);
          if (!permitted()) { retire("CONSENT_WITHDRAWN"); return; }
          if (!displayGptSlot({ googletag, elementId })) fail("GAM_SLOT_FAILED");
          else report("GPT_REQUEST_INVOKED");
        } catch { fail("GAM_RENDER_ERROR"); }
      });
    }).catch(() => fail("GAM_LOADER_FAILED"));
    return () => {
      cancelled = true; clearTimeout(timer); observer?.disconnect();
      window.removeEventListener("surgedome:consent-changed", consentChanged);
      cleanupSlot();
    };
  }, [decision, contract.advertisingAllowed, contract.personalizedAdvertisingAllowed, placementIdentifier]);
  if (!contract.advertisingAllowed || !decision.liveDeliveryApproved) return null;
  return <div ref={node} id={"gam-slot-" + decision.decisionId} style={{ maxWidth: "100%", overflow: "hidden" }} />;
}
